/**
 * GlobalIncomingCallUI  (ADDITIVE — INBOUND CALLING)
 *
 * Mounted once inside the authenticated shell, so incoming-call UI is available
 * on EVERY CRM route rather than only on /phone.
 *
 * Pieces:
 *   1. IncomingCallModal — the ringing dialog.
 *   2. On answer, the ActiveCallShell floating card shows the call controls. It
 *      carries a "View Details" button ONLY when the caller resolves to a CRM
 *      contact; pressing it opens the EXISTING ContactWorkspaceDrawer (Notes,
 *      Research, in-drawer call controls — ActiveCallInline), reused verbatim.
 *      The workspace never auto-opens — it opens only on that button press.
 *
 * Outgoing calls keep being rendered by the exact surfaces that render them
 * today (PhonePage's shell, the drawer's inline controls), untouched. Each
 * direction still has exactly one in-call owner: inbound is owned here.
 */

import { useEffect, useState } from 'react';
import ActiveCallShell from '../components/telephony/ActiveCallShell';
import IncomingCallModal from '../components/telephony/IncomingCallModal';
import ContactWorkspaceDrawer from '../components/contacts/ContactWorkspaceDrawer';
import { useTelephony } from './telephonyContext';

export function GlobalIncomingCallUI() {
  const { voice } = useTelephony();
  const { activeCall, status } = voice;

  // "View Details" is user-triggered ONLY — the workspace never auto-opens. It
  // opens when the rep presses "View Details" on the in-call controls, and the
  // floating call-control shell is shown at all other times. Reset when the
  // inbound call is fully over.
  const [workspaceOpen, setWorkspaceOpen] = useState(false);

  const isInbound = activeCall.kind !== 'idle' && activeCall.direction === 'inbound';

  useEffect(() => {
    if (!isInbound) setWorkspaceOpen(false);
  }, [isInbound]);

  // Feed the workspace recording policy through to the shell so the
  // mid-call InCallRecordingControl (auto-start on 'always', interactive
  // otherwise) mounts on the accepted-inbound floating card.
  const workspaceRecordingMode: 'off' | 'always' | undefined =
    status.kind === 'ready' ? status.recordingMode : undefined;

  // The caller's CRM contact id, once answered (present on in_call/ended/failed).
  const crmContactId =
    activeCall.kind === 'in_call' ||
    activeCall.kind === 'ended' ||
    activeCall.kind === 'failed'
      ? activeCall.contactId
      : undefined;

  // Button-driven: the workspace opens ONLY after the rep presses "View Details",
  // and only when the caller resolves to a CRM contact. It never auto-opens.
  const showWorkspace = isInbound && !!crmContactId && workspaceOpen;
  // The floating call-control card (which carries the "View Details" button) is
  // shown whenever the workspace isn't open.
  const showFloatingShell = isInbound && !showWorkspace;

  return (
    <>
      <IncomingCallModal />

      {showWorkspace && (
        <ContactWorkspaceDrawer
          open
          crmContactId={crmContactId ?? null}
          voice={voice}
          onClose={() => setWorkspaceOpen(false)}
        />
      )}

      {showFloatingShell && (
        <ActiveCallShell
          activeCall={activeCall}
          workspaceRecordingMode={workspaceRecordingMode}
          onMute={voice.mute}
          onUnmute={voice.unmute}
          onEndCall={voice.endCall}
          onDismiss={voice.clearActiveCall}
          // The full control set — the keypad and audio selectors that PhonePage
          // never wired up. Reusing the existing component, not reimplementing it.
          onSendDigit={voice.sendDigits}
          audioDevices={voice.audioDevices}
          onSelectInput={voice.setInputDevice}
          onSelectOutput={voice.setOutputDevice}
          // View Details — shown ONLY when the caller resolves to a CRM contact.
          // No crmContactId → prop is undefined → the button is hidden. Opens the
          // EXISTING ContactWorkspaceDrawer on press; never auto-opens.
          onViewDetails={crmContactId ? () => setWorkspaceOpen(true) : undefined}
        />
      )}
    </>
  );
}

export default GlobalIncomingCallUI;
